/**
 * Lighting maths for the remastered sprites: one light from the top left, Lambert shading, and an
 * ordered dither between ramp steps so curved surfaces read round without banding. Pure.
 */

export type Vec3 = readonly [number, number, number];

export function normalize(v: Vec3): Vec3 {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

/** Toward the light: up and to the left, and toward the viewer (y grows down). */
export const LIGHT = normalize([-0.55, -0.75, 0.95]);

export function lambert(n: Vec3, light: Vec3 = LIGHT): number {
  return Math.max(0, n[0] * light[0] + n[1] * light[1] + n[2] * light[2]);
}

const BAYER4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

/** -0.5..0.5, the 4 x 4 ordered-dither threshold at a pixel. */
export function bayer(x: number, y: number): number {
  return (BAYER4[(y & 3) * 4 + (x & 3)] ?? 0) / 16 - 0.47;
}

/** Light intensity 0..1 to a ramp index lo..hi, dithered by `amount` of a step. */
export function step(intensity: number, x: number, y: number, lo = 0, hi = 4, amount = 0.6): number {
  const v = lo + intensity * (hi - lo) + bayer(x, y) * amount;
  return Math.max(lo, Math.min(hi, Math.round(v)));
}

/** A normal on an ellipsoid bulging toward the viewer, or null outside it. */
export function domeNormal(x: number, y: number, cx: number, cy: number, rx: number, ry: number): Vec3 | null {
  const dx = (x + 0.5 - cx) / rx;
  const dy = (y + 0.5 - cy) / ry;
  const d = dx * dx + dy * dy;
  if (d > 1) return null;
  return normalize([dx, dy, Math.sqrt(1 - d) + 0.15]);
}

/** A stable hash for texture: the same (x, y, n) always gives the same 32 bits. */
export function hash3(x: number, y: number, n: number): number {
  let h = Math.imul(x + 0x27d4eb2d, 0x165667b1) ^ Math.imul(y + 0x61c88647, 0x27d4eb2f) ^ Math.imul(n + 1, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  return (h ^ (h >>> 13)) >>> 0;
}

/** Value noise 0..1 that repeats every `period` lattice cells: tiles seamlessly cell to cell. */
export function tileNoise(x: number, y: number, cell: number, period: number, seed: number): number {
  const fx = x / cell;
  const fy = y / cell;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const sx = smooth(fx - x0);
  const sy = smooth(fy - y0);
  const v = (i: number, j: number): number => (hash3(((i % period) + period) % period, ((j % period) + period) % period, seed) % 1000) / 999;
  const top = v(x0, y0) + (v(x0 + 1, y0) - v(x0, y0)) * sx;
  const bottom = v(x0, y0 + 1) + (v(x0 + 1, y0 + 1) - v(x0, y0 + 1)) * sx;
  return top + (bottom - top) * sy;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}
